import { type TreeProps } from 'antd';
import { isArray } from '@dimjs/lang';
import { type API, type ModelType } from '@dimjs/model';
import { Model } from '@dimjs/model-react';
import type { TAny } from '@flatbiz/utils';
import {
  arrayToMap,
  type TPlainObject,
  treeToArray,
  type TSetDefaultDefined,
} from '@flatbiz/utils';
import type { TRequestStatus } from '../request-status/index.js';

export type ModelState = {
  treeList: TSetDefaultDefined<TreeProps['treeData'], []>;
  treeTiledArray: TPlainObject[];
  treeTiledArrayMap: TPlainObject;
  queryIsEmpty: boolean;
  requestStatus?: TRequestStatus;
  requestErrorMessage?: string;
};

type ModelActionParams = {
  setTreeList: {
    treeList: ModelState['treeList'];
    childrenName: string;
    valueName: string;
  };
  resetTreeList: void;
  changeRequestStatus: {
    status: TRequestStatus;
    errorMessage?: string;
  };
  treeListAppendChildren: {
    value: string | number;
    appendList: TPlainObject[];
    childrenName: string;
    valueName: string;
  };
};

const defaultState: ModelState = {
  treeList: [],
  treeTiledArray: [],
  treeTiledArrayMap: {},
  queryIsEmpty: false,
};

const TreeWrapperModel: ModelType<ModelState, ModelActionParams> = {
  actions: {
    setTreeList: (params) => {
      return (state) => {
        state.treeList = params.treeList || [];
        state.treeTiledArray = treeToArray(state.treeList, params.childrenName);
        state.treeTiledArrayMap = arrayToMap(
          state.treeTiledArray,
          params.valueName
        );
        state.requestStatus = 'request-success';
      };
    },
    resetTreeList: () => {
      return (state) => {
        state.treeList = [];
        state.treeTiledArray = [];
        state.treeTiledArrayMap = {};
      };
    },
    changeRequestStatus: (params) => {
      return (state) => {
        state.requestStatus = params.status;
        if (params.status === 'request-error') {
          state.treeList = [];
          state.treeTiledArray = [];
          state.treeTiledArrayMap = {};
          state.requestErrorMessage = params.errorMessage || '数据查询异常';
        } else {
          state.requestErrorMessage = undefined;
        }
      };
    },
    treeListAppendChildren: (params) => {
      return (state) => {
        const tiledArray = treeToArray(state.treeList, params.childrenName);
        const treeTiledArrayMap = arrayToMap(tiledArray, params.valueName);
        const target = treeTiledArrayMap[params.value];
        if (target) {
          if (isArray(params.appendList) && params.appendList.length > 0) {
            target[params.childrenName] = params.appendList;
          } else {
            target.isLeaf = true;
          }
        }
        state.treeTiledArray = tiledArray;
        state.treeTiledArrayMap = treeTiledArrayMap;
      };
    },
  },
  state: defaultState,
};

const treeWrapperModels: Record<
  string,
  API<ModelType<ModelState, ModelActionParams, TAny>>
> = {};

/**
 * ```
 * 使用方式
 * const [state, actions] = useTreeWrapperModel('key值').useStore();
 * ```
 */
export const treeWrapperModel = (key: string) => {
  if (!treeWrapperModels[key]) {
    treeWrapperModels[key] = Model(TreeWrapperModel);
  }
  return treeWrapperModels[key];
};
